/*
    This file uses code from @react-three/drei.
    Original Copyright (c) 2020 react-spring
    Source: https://github.com/pmndrs/drei
    License: MIT (see Frontend/src/THIRD-PARTY-LICENSES/drei-MIT.txt)
*/

import { type ThreeEvent, useThree } from '@react-three/fiber';
import { useMemo, useRef, useState } from 'react';
import { Vector3, CanvasTexture, type Mesh } from 'three';

import { useCustomGizmoHelperContext } from 'app/modules/wallforming/components/scenes/camera-controls/CustomGizmoHelper';

interface CustomGizmoViewcubeProps {
    faces?: string[];
    upVectors?: Record<string, Vector3>; // key = face name (e.g., "Front"), value = up vector for the face
    backgroundColor?: string;
    hoverColor?: string;
    textColor?: string;
    strokeColor?: string;
    font?: string;
    opacity?: number;
    onPointerDown?: (e: ThreeEvent<PointerEvent>) => void;
}

const defaultColors = {
    background: '#d8d9df',
    hover: '#a4a5a8',
    text: '#000000',
    stroke: '#000000'
};

const defaultFaces = ['Right', 'Left', 'Top', 'Bottom', 'Front', 'Back'];
const defaultFont = 'bold 30px Inter, Arial, sans-serif';

const convertToPositionVector = (xyz: number[]) => new Vector3(...xyz).multiplyScalar(0.38);

const corners = [
    [1, 1, 1],
    [1, 1, -1],
    [1, -1, 1],
    [1, -1, -1],
    [-1, 1, 1],
    [-1, 1, -1],
    [-1, -1, 1],
    [-1, -1, -1]
].map(convertToPositionVector);

const cornerDimensions: [number, number, number] = [0.25, 0.25, 0.25];

const edges = [
    [1, 1, 0],
    [1, 0, 1],
    [1, 0, -1],
    [1, -1, 0],
    [0, 1, 1],
    [0, 1, -1],
    [0, -1, 1],
    [0, -1, -1],
    [-1, 1, 0],
    [-1, 0, 1],
    [-1, 0, -1],
    [-1, -1, 0]
].map(convertToPositionVector);

const edgeDimensions = edges.map(
    (edge) => edge.toArray().map((axis) => (axis === 0 ? 0.5 : 0.25)) as [number, number, number]
);

export const CustomGizmoViewcube = (props: CustomGizmoViewcubeProps) => {
    return (
        <group scale={[60, 60, 60]}>
            <FaceCube {...props} />
            {edges.map((edge, index) => (
                <EdgeCube
                    key={index}
                    dimensions={edgeDimensions[index]}
                    hoverColor={props.hoverColor}
                    position={edge}
                />
            ))}
            {corners.map((corner, index) => (
                <EdgeCube key={index} dimensions={cornerDimensions} hoverColor={props.hoverColor} position={corner} />
            ))}
        </group>
    );
};

interface FaceMaterialProps {
    hover: boolean;
    index: number;
    faces?: string[];
    backgroundColor?: string;
    hoverColor?: string;
    textColor?: string;
    strokeColor?: string;
    font?: string;
    opacity?: number;
}

const FaceMaterial = ({
    hover,
    index,
    faces = defaultFaces,
    backgroundColor = defaultColors.background,
    hoverColor = defaultColors.hover,
    textColor = defaultColors.text,
    strokeColor = defaultColors.stroke,
    font = defaultFont,
    opacity = 1
}: FaceMaterialProps) => {
    const gl = useThree((state) => state.gl);

    const texture = useMemo(() => {
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 128;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = strokeColor;
        ctx.strokeRect(0, 0, canvas.width, canvas.height);
        ctx.font = font;
        ctx.textAlign = 'center';
        ctx.fillStyle = textColor;
        ctx.fillText(faces[index], 64, 76);

        return new CanvasTexture(canvas);
    }, [backgroundColor, strokeColor, font, textColor, faces, index]);

    return (
        <meshBasicMaterial
            attach={`material-${index}`}
            color={hover ? hoverColor : 'white'}
            map={texture}
            map-anisotropy={gl.capabilities.getMaxAnisotropy() || 1}
            opacity={opacity}
            transparent
        />
    );
};

const FaceCube = (props: CustomGizmoViewcubeProps) => {
    const meshRef = useRef<Mesh | null>(null);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const gl = useThree((state) => state.gl);

    const { tweenCamera } = useCustomGizmoHelperContext();

    const handleOnPointerOut = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        setHoveredIndex(null);
        gl.domElement.style.setProperty('cursor', 'auto');
    };

    const handleOnPointerMove = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        setHoveredIndex(Math.floor(e.faceIndex! / 2));
        gl.domElement.style.setProperty('cursor', 'pointer');
    };

    const handleOnPointerDown = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();

        if (props.onPointerDown) {
            props.onPointerDown(e);
        } else {
            if (!e.face?.normal) {
                return;
            }

            let up: Vector3 | undefined = undefined;
            if (props.upVectors) {
                const faceIndex = Math.floor(e.faceIndex! / 2);
                const faceName = props.faces ? props.faces[faceIndex] : defaultFaces[faceIndex];
                up = props.upVectors[faceName];
            }

            tweenCamera(e.face.normal, up);
        }
    };

    return (
        <mesh
            ref={meshRef}
            onDoubleClick={(e) => e.nativeEvent.stopImmediatePropagation()}
            onPointerDown={handleOnPointerDown}
            onPointerMove={handleOnPointerMove}
            onPointerOut={handleOnPointerOut}
        >
            {[...Array(6)].map((_, index) => (
                <FaceMaterial key={index} hover={hoveredIndex === index} index={index} {...props} />
            ))}
            <boxGeometry />
        </mesh>
    );
};

interface EdgeCubeProps {
    dimensions: [number, number, number];
    position: Vector3;
    hoverColor?: string;
    onPointerDown?: (e: ThreeEvent<PointerEvent>) => void;
}

const EdgeCube = ({ dimensions, position, hoverColor = defaultColors.hover, onPointerDown }: EdgeCubeProps) => {
    const { tweenCamera } = useCustomGizmoHelperContext();
    const [hover, setHover] = useState<boolean>(false);
    const gl = useThree((state) => state.gl);

    const handleOnPointerOut = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        setHover(false);
        gl.domElement.style.setProperty('cursor', 'auto');
    };

    const handleOnPointerMove = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        setHover(true);
        gl.domElement.style.setProperty('cursor', 'pointer');
    };

    const handleOnPointerDown = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();

        if (onPointerDown) {
            onPointerDown(e);
        } else {
            tweenCamera(position);
        }
    };

    return (
        <mesh
            position={position}
            scale={1.01}
            onDoubleClick={(e) => e.nativeEvent.stopImmediatePropagation()}
            onPointerDown={handleOnPointerDown}
            onPointerOut={handleOnPointerOut}
            onPointerOver={handleOnPointerMove}
        >
            <meshBasicMaterial color={hover ? hoverColor : 'white'} opacity={0.8} visible={hover} transparent />
            <boxGeometry args={dimensions} />
        </mesh>
    );
};
